import React from 'react';
import type { Session, SupabaseClient } from '@supabase/supabase-js';
import { useLocation, useNavigate } from 'react-router-dom';
import useSessionStore from '../state/session';
import { AppError, isAppError, toAppError } from '../utils/appErrors';
import { SUPABASE_AUTH_STORAGE_KEY } from '../lib/supabaseClient';
import { clearPrincipalOwnedState } from './sessionCleanup';
import {
  currentRelativePath,
  isAnonymousTransportPath,
  isPublicAppPath,
  storeReturnPath,
} from './routePolicy';
import {
  registerLogoutHandler,
  registerSessionFailureHandler,
  reportSessionFailure,
  setAuthTransportExpectation,
  type LogoutRequest,
  type SessionValidationReason,
} from './sessionSignals';
import {
  clearActivePrincipalQueries,
  clearActivePrincipalQueryData,
  refetchActiveStaleQueries,
} from './queryClientLifecycle';

const VALIDATION_TIMEOUT_MS = 10_000;
const PERIODIC_VALIDATION_MS = 5 * 60_000;
const RESUME_VALIDATION_AGE_MS = 60_000;
const FORBIDDEN_REVALIDATION_COOLDOWN_MS = 10_000;

const DEFINITIVE_AUTH_CODES = new Set([
  'bad_jwt',
  'invalid_jwt',
  'invalid_refresh_token',
  'refresh_token_not_found',
  'session_not_found',
  'user_not_found',
]);

const DEFINITIVE_AUTH_ERROR_NAMES = new Set([
  'AuthSessionMissingError',
  'AuthInvalidJwtError',
  'AuthInvalidTokenResponseError',
]);

class SessionValidationTimeoutError extends Error {
  constructor() {
    super('Session validation timed out');
    this.name = 'SessionValidationTimeoutError';
  }
}

type CoordinatorProps = {
  children: React.ReactNode;
  supabase: SupabaseClient;
  disabled?: boolean;
};

type ValidationOptions = {
  initial?: boolean;
  queueAfterCurrent?: boolean;
};

type PendingValidation = {
  reason: SessionValidationReason;
  options: Pick<ValidationOptions, 'initial'>;
};

function errorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const raw = (error as { status?: unknown; statusCode?: unknown }).status
    ?? (error as { statusCode?: unknown }).statusCode;
  const parsed = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function errorCode(error: unknown): string {
  if (!error || typeof error !== 'object') return '';
  const value = (error as { code?: unknown }).code;
  return typeof value === 'string' ? value.toLowerCase() : '';
}

function errorName(error: unknown): string {
  if (!error || typeof error !== 'object') return '';
  const value = (error as { name?: unknown }).name;
  return typeof value === 'string' ? value : '';
}

function isRetryableAuthFetchError(error: unknown): boolean {
  return Boolean(
    error
    && typeof error === 'object'
    && (error as { name?: unknown }).name === 'AuthRetryableFetchError'
  );
}

export function isDefinitiveSessionFailure(error: unknown): boolean {
  if (isAppError(error)) return error.kind === 'session_expired';
  if (isRetryableAuthFetchError(error)) return false;
  const status = errorStatus(error);
  const code = errorCode(error);
  const name = errorName(error);
  return status === 401
    || status === 403
    || DEFINITIVE_AUTH_CODES.has(code)
    || DEFINITIVE_AUTH_ERROR_NAMES.has(name);
}

function unavailableError(error: unknown): AppError {
  if (error instanceof SessionValidationTimeoutError) {
    return new AppError(
      'service_unavailable',
      'We could not verify your session. Please try again.',
      { context: 'session_validation_timeout', cause: error },
    );
  }
  const classified = toAppError(error, 'We could not verify your session.', 'session_validation');
  if (classified.kind === 'network' || classified.kind === 'service_unavailable') return classified;
  return new AppError(
    'service_unavailable',
    'We could not verify your session. Please try again.',
    { context: 'session_validation', cause: error },
  );
}

function withTimeout<T>(operation: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = globalThis.setTimeout(
      () => reject(new SessionValidationTimeoutError()),
      timeoutMs,
    );
    operation.then(
      (value) => {
        globalThis.clearTimeout(timeoutId);
        resolve(value);
      },
      (error) => {
        globalThis.clearTimeout(timeoutId);
        reject(error);
      },
    );
  });
}

function shouldRefetchAfterValidation(reason: SessionValidationReason): boolean {
  return reason === 'focus'
    || reason === 'visibility'
    || reason === 'online'
    || reason === 'manual_retry';
}

function scheduleStaleQueryRefreshAfterValidation(reason: SessionValidationReason): void {
  if (!shouldRefetchAfterValidation(reason)) return;
  // Session verification owns only the auth result. Page-data refreshes can be
  // slow or fail independently and must never turn a valid session into the
  // background-verification warning. React Query keeps each page's existing
  // data mounted while this best-effort refresh runs separately.
  void refetchActiveStaleQueries().catch(() => undefined);
}

function isPassiveSessionMaintenance(reason: SessionValidationReason): boolean {
  return reason === 'focus'
    || reason === 'visibility'
    || reason === 'online'
    || reason === 'interval';
}

export function AuthSessionCoordinator({
  children,
  supabase,
  disabled = false,
}: CoordinatorProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const navigateRef = React.useRef(navigate);
  const locationRef = React.useRef(location);
  const singleFlightRef = React.useRef<Promise<void> | null>(null);
  const pendingValidationRef = React.useRef<PendingValidation | null>(null);
  const lastValidatedAtRef = React.useRef(0);
  const validationGenerationRef = React.useRef(0);
  const teardownInFlightRef = React.useRef<Promise<void> | null>(null);
  const protectedTransportBlockedRef = React.useRef(false);
  const confirmedForbiddenRef = React.useRef<{
    accessToken: string;
    principalEpoch: number;
    validatedAt: number;
  } | null>(null);
  const mountedRef = React.useRef(true);
  const sessionAccessToken = useSessionStore((state) => state.session?.access_token ?? null);

  React.useEffect(() => {
    navigateRef.current = navigate;
    locationRef.current = location;
    if (protectedTransportBlockedRef.current) return;
    const anonymousTransport = isAnonymousTransportPath(location.pathname);
    setAuthTransportExpectation(anonymousTransport ? 'public' : 'session', sessionAccessToken);
  }, [location, navigate, sessionAccessToken]);

  const teardownPrincipal = React.useCallback(async (request: LogoutRequest) => {
    if (teardownInFlightRef.current) return teardownInFlightRef.current;
    const currentState = useSessionStore.getState();
    if (
      request.reason === 'session_expired'
      && !currentState.session?.user?.id
      && currentState.authStatus === 'unauthenticated'
    ) return;
    const teardown = Promise.resolve().then(async () => {
      confirmedForbiddenRef.current = null;
      validationGenerationRef.current += 1;
      pendingValidationRef.current = null;
      const currentLocation = locationRef.current;
      const publicRoute = isPublicAppPath(currentLocation.pathname);
      const anonymousTransport = isAnonymousTransportPath(currentLocation.pathname);
      if (request.reason === 'session_expired' && !publicRoute) {
        storeReturnPath(currentRelativePath(currentLocation));
      }

      protectedTransportBlockedRef.current = !anonymousTransport;
      setAuthTransportExpectation(anonymousTransport ? 'public' : 'blocked', null);
      // Change the epoch synchronously before awaiting any cleanup. This
      // unmounts old-principal components and prevents late completions from
      // committing while query/channel teardown settles.
      const stateBeforeClear = useSessionStore.getState();
      const hadCommittedPrincipal = Boolean(stateBeforeClear.session?.user?.id);
      // Clear identity and hold the checking state atomically. A transient
      // unauthenticated render would allow new credentials before local
      // sign-out has finished removing the previous client session.
      stateBeforeClear.setSession(null, { authStatus: 'checking' });
      if (!hadCommittedPrincipal) {
        // Cold boot can discover an invalid persisted Supabase session before
        // it has ever been committed to the Zustand store. That is still a
        // confirmed principal boundary: remount the QueryClient/public-link
        // surface so a request started with the stale token cannot survive.
        useSessionStore.setState((state) => ({
          principalEpoch: state.principalEpoch + 1,
        }));
      }
      const queryCleanup = clearActivePrincipalQueries();
      await queryCleanup;
      try { await supabase.removeAllChannels(); } catch { /* best effort */ }
      await clearPrincipalOwnedState(SUPABASE_AUTH_STORAGE_KEY);

      validationGenerationRef.current += 1;
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch {
        // Exact local auth storage was already removed above.
      }
      validationGenerationRef.current += 1;

      if (!mountedRef.current) return;
      useSessionStore.getState().setAuthStatus('unauthenticated');
      if (request.preservePublicRoute || publicRoute) {
        protectedTransportBlockedRef.current = false;
        setAuthTransportExpectation(anonymousTransport ? 'public' : 'session', null);
        return;
      }
      navigateRef.current(
        request.reason === 'session_expired'
          ? '/login?reason=session_expired'
          : stateBeforeClear.session?.user?.app_metadata?.provider === 'email' || ['guest', 'freelancer'].includes(stateBeforeClear.securityLevel || '') ? '/login' : '/login?sso=1',
        { replace: true },
      );
      protectedTransportBlockedRef.current = false;
    }).finally(() => {
      teardownInFlightRef.current = null;
    });
    teardownInFlightRef.current = teardown;
    return teardown;
  }, [supabase]);

  const validateSession = React.useCallback((
    reason: SessionValidationReason,
    options: ValidationOptions = {},
  ): Promise<void> => {
    if (disabled) return Promise.resolve();
    // Teardown owns the auth client until local sign-out and cache cleanup
    // finish. Revalidation here could read the still-persisted old token and
    // resurrect the principal that is being removed.
    if (teardownInFlightRef.current) return teardownInFlightRef.current;
    if (singleFlightRef.current) {
      if (options.queueAfterCurrent) {
        pendingValidationRef.current = {
          reason,
          options: { initial: options.initial },
        };
      }
      return singleFlightRef.current;
    }

    const validationGeneration = ++validationGenerationRef.current;
    const stateAtValidationStart = useSessionStore.getState();
    const backgroundPrincipalUserId = stateAtValidationStart.session?.user?.id ?? null;
    const backgroundPrincipalEpoch = stateAtValidationStart.principalEpoch;
    const preserveMountedPrincipal = Boolean(
      !options.initial
      && reason !== 'authorization_changed'
      && backgroundPrincipalUserId
      && stateAtValidationStart.authStatus === 'authenticated'
      && stateAtValidationStart.contextStatus === 'ready'
      && stateAtValidationStart.sessionReady,
    );
    const validation = withTimeout((async () => {
      const store = useSessionStore.getState();
      if (!preserveMountedPrincipal) {
        store.setAuthStatus('checking');
        store.setAuthIssue(null);
        if (reason === 'authorization_changed') {
          store.invalidateContextLoad();
          await clearActivePrincipalQueryData();
          await clearPrincipalOwnedState(null);
        }
      }

      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (validationGeneration !== validationGenerationRef.current) return;
      if (sessionError) throw sessionError;

      const session = sessionData.session;
      if (!session?.access_token || !session.user?.id) {
        await teardownPrincipal({
          reason: 'session_expired',
          preservePublicRoute: isPublicAppPath(locationRef.current.pathname),
        });
        return;
      }

      const { data: userData, error: userError } = await supabase.auth.getUser(session.access_token);
      if (validationGeneration !== validationGenerationRef.current) return;
      if (userError) throw userError;
      if (!userData.user?.id || userData.user.id !== session.user.id) {
        throw new AppError(
          'session_expired',
          'Your session expired. Sign in again to continue.',
          { context: 'session_user_mismatch' },
        );
      }

      // Only native email accounts use Portal's optional authenticator step.
      // Employee Microsoft/Entra sessions keep the existing validation path.
      if (userData.user.app_metadata?.provider === 'email' && userData.user.factors?.some(f => f.status === 'verified')) {
        const { data: assurance, error: assuranceError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (assuranceError) throw assuranceError;
        if (assurance?.currentLevel !== 'aal2') {
          await teardownPrincipal({ reason: 'session_expired', preservePublicRoute: false });
          return;
        }
      }

      const authoritativeSession: Session = {
        ...session,
        user: userData.user,
      };
      const currentBeforeCommit = useSessionStore.getState();
      const previousUserId = currentBeforeCommit.session?.user?.id ?? null;
      const canCommitInBackground = Boolean(
        preserveMountedPrincipal
        && previousUserId === authoritativeSession.user.id
        && currentBeforeCommit.principalEpoch === backgroundPrincipalEpoch
        && currentBeforeCommit.authStatus === 'authenticated'
        && currentBeforeCommit.contextStatus === 'ready'
        && currentBeforeCommit.sessionReady,
      );
      if (canCommitInBackground) {
        protectedTransportBlockedRef.current = false;
        setAuthTransportExpectation(
          isAnonymousTransportPath(locationRef.current.pathname) ? 'public' : 'session',
          authoritativeSession.access_token,
        );
        currentBeforeCommit.replaceSessionForCurrentPrincipal(authoritativeSession);
        currentBeforeCommit.setAuthIssue(null);
        lastValidatedAtRef.current = Date.now();

        scheduleStaleQueryRefreshAfterValidation(reason);
        return;
      }
      let sessionCommittedForTransition = false;
      if (previousUserId && previousUserId !== authoritativeSession.user.id) {
        const anonymousTransport = isAnonymousTransportPath(locationRef.current.pathname);
        protectedTransportBlockedRef.current = !anonymousTransport;
        setAuthTransportExpectation(anonymousTransport ? 'public' : 'blocked', null);
        // Commit the new identity and epoch before awaiting old-principal
        // cleanup. Context remains loading, so protected children stay gated.
        useSessionStore.getState().setSession(authoritativeSession);
        sessionCommittedForTransition = true;
        const queryCleanup = clearActivePrincipalQueries();
        await queryCleanup;
        try { await supabase.removeAllChannels(); } catch { /* best effort */ }
        await clearPrincipalOwnedState(null);
        if (validationGeneration !== validationGenerationRef.current) return;
      }
      protectedTransportBlockedRef.current = false;
      setAuthTransportExpectation(
        isAnonymousTransportPath(locationRef.current.pathname) ? 'public' : 'session',
        authoritativeSession.access_token,
      );
      if (!sessionCommittedForTransition) {
        useSessionStore.getState().setSession(authoritativeSession);
      }
      await useSessionStore.getState().loadContext(supabase);
      if (validationGeneration !== validationGenerationRef.current) return;

      const current = useSessionStore.getState();
      if (
        current.session?.user?.id !== authoritativeSession.user.id
        || current.contextStatus !== 'ready'
      ) return;
      current.setAuthStatus('authenticated');
      current.setAuthIssue(null);
      lastValidatedAtRef.current = Date.now();

      if (!options.initial) scheduleStaleQueryRefreshAfterValidation(reason);
    })(), VALIDATION_TIMEOUT_MS).catch(async (error: unknown) => {
      if (validationGeneration !== validationGenerationRef.current) return;
      if (isDefinitiveSessionFailure(error)) {
        await teardownPrincipal({
          reason: 'session_expired',
          preservePublicRoute: isPublicAppPath(locationRef.current.pathname),
        });
        return;
      }

      // Invalidate the still-running operation behind Promise.race. Each
      // awaited phase checks this generation before it can commit state.
      validationGenerationRef.current += 1;
      const issue = isAppError(error)
        ? error
        : unavailableError(error);
      const store = useSessionStore.getState();
      const canKeepMountedPrincipal = Boolean(
        preserveMountedPrincipal
        && backgroundPrincipalUserId
        && store.session?.user?.id === backgroundPrincipalUserId
        && store.principalEpoch === backgroundPrincipalEpoch
        && store.authStatus === 'authenticated'
        && store.contextStatus === 'ready'
        && store.sessionReady,
      );
      if (canKeepMountedPrincipal) {
        // A network failure or timeout is not proof that the current session
        // ended. Keep the editor mounted and surface a retryable warning while
        // existing protected requests continue to be enforced by the server.
        store.setAuthIssue(issue);
        return;
      }
      store.invalidateContextLoad();
      store.setAuthIssue(issue);
      if (issue.kind === 'access_denied') {
        store.setAuthStatus('authenticated');
      } else {
        store.setAuthStatus('unavailable');
      }
    }).finally(() => {
      if (singleFlightRef.current !== validation) return;
      singleFlightRef.current = null;
      const pendingValidation = pendingValidationRef.current;
      pendingValidationRef.current = null;
      if (
        !pendingValidation
        || !mountedRef.current
        || teardownInFlightRef.current
      ) return;
      // Start the queued auth-event validation after this promise has fully
      // settled. This remains outside Supabase's synchronous auth callback and
      // prevents an old single-flight operation from swallowing a user/token
      // transition.
      queueMicrotask(() => {
        if (!mountedRef.current || teardownInFlightRef.current) return;
        void validateSession(pendingValidation.reason, pendingValidation.options);
      });
    });
    singleFlightRef.current = validation;
    return validation;
  }, [disabled, supabase, teardownPrincipal]);

  React.useEffect(() => {
    mountedRef.current = true;
    const unregisterLogout = registerLogoutHandler(teardownPrincipal);
    if (disabled) {
      return () => {
        mountedRef.current = false;
        unregisterLogout();
      };
    }

    const unregisterFailure = registerSessionFailureHandler((signal) => {
      if (teardownInFlightRef.current) return;
      const stateAtFailure = useSessionStore.getState();
      if (
        isPassiveSessionMaintenance(signal.reason)
        && isAnonymousTransportPath(locationRef.current.pathname)
        && !stateAtFailure.session?.user?.id
      ) {
        // Public token pages have no Supabase principal by design. Focus,
        // visibility, reconnect, and interval checks must not reinterpret that
        // expected absence as an expired employee session and remount an
        // in-progress guest document.
        setAuthTransportExpectation('public', null);
        return;
      }
      if (signal.reason !== 'protected_request_forbidden') {
        void validateSession(signal.reason, { queueAfterCurrent: signal.reason === 'authorization_changed' });
        return;
      }

      const accessToken = stateAtFailure.session?.access_token ?? null;
      const principalEpoch = stateAtFailure.principalEpoch;
      const confirmed = confirmedForbiddenRef.current;
      if (
        accessToken
        && confirmed?.accessToken === accessToken
        && confirmed.principalEpoch === principalEpoch
        && Date.now() - confirmed.validatedAt < FORBIDDEN_REVALIDATION_COOLDOWN_MS
      ) {
        // A 403 from the same principal immediately after authoritative
        // validation is an authorization result, not a reason to remount the
        // shell and execute the same forbidden query in a validation loop.
        return;
      }

      void validateSession(signal.reason).then(() => {
        const current = useSessionStore.getState();
        if (
          accessToken
          && current.session?.access_token === accessToken
          && current.principalEpoch === principalEpoch
          && current.authStatus === 'authenticated'
          && current.contextStatus === 'ready'
        ) {
          confirmedForbiddenRef.current = {
            accessToken,
            principalEpoch,
            validatedAt: Date.now(),
          };
        }
      });
    });

    // Supabase warns against awaiting other auth methods while its auth lock is
    // held. Keep this callback synchronous and queue validation after it exits.
    const { data: authListener } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (teardownInFlightRef.current) return;
      const anonymousTransport = isAnonymousTransportPath(locationRef.current.pathname);
      const currentSession = useSessionStore.getState().session;
      const currentUserId = currentSession?.user?.id ?? null;
      if (event === 'INITIAL_SESSION' && !nextSession && !currentSession
          && useSessionStore.getState().authStatus === 'unauthenticated') return;
      const currentAccessToken = currentSession?.access_token ?? null;
      const nextUserId = nextSession?.user?.id ?? null;
      const nextAccessToken = nextSession?.access_token ?? null;
      const authIdentityOrTokenChanged = (
        event === 'SIGNED_IN'
        || event === 'TOKEN_REFRESHED'
      ) && (
        currentUserId !== nextUserId
        || currentAccessToken !== nextAccessToken
      );
      const samePrincipalRefresh = event === 'TOKEN_REFRESHED'
        && Boolean(currentUserId)
        && currentUserId === nextUserId;
      const sameReadyPrincipal = Boolean(
        currentUserId
        && currentUserId === nextUserId
        && currentSession
        && nextSession
        && useSessionStore.getState().authStatus === 'authenticated'
        && useSessionStore.getState().contextStatus === 'ready'
        && useSessionStore.getState().sessionReady,
      );
      if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        if (authIdentityOrTokenChanged) {
          confirmedForbiddenRef.current = null;
          // Invalidate any getSession/getUser/context operation that captured
          // the previous identity or access token. The queued microtask below
          // asks for a guaranteed follow-up instead of coalescing into it.
          validationGenerationRef.current += 1;
        }
        if (sameReadyPrincipal && nextSession) {
          // Supabase rotates tokens in the background and can also emit
          // same-user SIGNED_IN events when a tab regains focus. Update the
          // transport token without gating or remounting the protected shell.
          useSessionStore.getState().replaceSessionForCurrentPrincipal(nextSession);
          protectedTransportBlockedRef.current = false;
          setAuthTransportExpectation(
            anonymousTransport ? 'public' : 'session',
            nextAccessToken,
          );
        } else {
          protectedTransportBlockedRef.current = !anonymousTransport && !samePrincipalRefresh;
          setAuthTransportExpectation(
            anonymousTransport ? 'public' : samePrincipalRefresh ? 'session' : 'blocked',
            anonymousTransport || samePrincipalRefresh ? nextAccessToken : null,
          );
          useSessionStore.getState().setAuthStatus('checking');
        }
      }
      queueMicrotask(() => {
        if (!mountedRef.current) return;
        if (event === 'SIGNED_OUT') {
          if (useSessionStore.getState().session?.user?.id) {
            void teardownPrincipal({
              reason: 'session_expired',
              preservePublicRoute: isPublicAppPath(locationRef.current.pathname),
            });
          }
          return;
        }
        if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          void validateSession('auth_state_change', {
            queueAfterCurrent: authIdentityOrTokenChanged,
          });
        }
      });
    });

    void validateSession('auth_state_change', { initial: true });

    const validateIfStale = (reason: 'focus' | 'visibility') => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastValidatedAtRef.current < RESUME_VALIDATION_AGE_MS) return;
      reportSessionFailure({ reason, source: 'coordinator' });
    };
    const onFocus = () => validateIfStale('focus');
    const onVisibility = () => validateIfStale('visibility');
    const onOnline = () => reportSessionFailure({ reason: 'online', source: 'coordinator' });
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        reportSessionFailure({ reason: 'interval', source: 'coordinator' });
      }
    }, PERIODIC_VALIDATION_MS);

    return () => {
      mountedRef.current = false;
      validationGenerationRef.current += 1;
      pendingValidationRef.current = null;
      unregisterFailure();
      unregisterLogout();
      authListener.subscription.unsubscribe();
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      window.clearInterval(intervalId);
    };
  }, [disabled, supabase, teardownPrincipal, validateSession]);

  return <>{children}</>;
}

export function requestSessionRetry(): void {
  reportSessionFailure({ reason: 'manual_retry', source: 'coordinator' });
}
